/**
 * LeadFocoPage — Modo Foco (Fase 2).
 *
 * Vista alternativa ao Kanban tradicional: apresenta 1 lead grande no ecrã
 * de cada vez, ordenada pela prioridade de IA. O consultor concentra-se
 * numa lead, contacta (com auto-log), regista situações rápidas e evolui
 * — a próxima aparece automaticamente sem sair do modo.
 *
 * Atalhos de teclado:
 *   ←  N  → lead anterior
 *   →  M  → lead seguinte
 *   Esc  → volta ao Kanban
 *
 * Reutiliza:
 *   * Endpoints /api/leads/list, /api/leads/events, /api/leads/results
 *   * `EvolvePanel` do Kanban (com chips + observações compostas)
 *   * `logAttempt` para auto-registo em tel/WA/email
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  SpinnerGap, ArrowLeft, Phone, Envelope, PhoneSlash, PhoneIncoming,
  Clock, X, CaretLeft, CaretRight, ArrowsClockwise,
} from '@phosphor-icons/react';
import WhatsAppLink from '@/components/WhatsAppLink';
import ScopeToggle from '@/components/ScopeToggle';
import useScopeFilter from '@/hooks/useScopeFilter';
import { EvolvePanel } from './LeadKanbanPage';
import { PersonLink } from '@/components/PersonLink';

async function logAttempt(idNegocio, tipo, obs) {
  try { await api.post(`/negocios/${idNegocio}/attempt`, { tipo, obs: obs || null }); }
  catch (_) { /* silent */ }
}

const TIPO_LABELS = { CS: 'Consultores', BC: 'B2C', BB: 'B2B' };

export default function LeadFocoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { scope, setScope } = useScopeFilter();
  const [tipoLead, setTipoLead] = useState('CS');
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [attemptCounts, setAttemptCounts] = useState({});
  const [chipsCatalog, setChipsCatalog] = useState([]);
  const [chipsSel, setChipsSel] = useState(new Set());
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [le, ev, re] = await Promise.all([
        api.post('/leads/list', { tipo: tipoLead, scope }),
        api.post('/leads/events', { tipo: tipoLead }),
        api.post('/leads/results'),
      ]);
      // Ordena leads: com scoreAI DESC, depois por DataRegisto ASC (mais antigas primeiro)
      const arr = (le.data?.leads || []).slice().sort((a, b) => {
        const sa = a.AIPriorityScore ?? -1;
        const sb = b.AIPriorityScore ?? -1;
        if (sb !== sa) return sb - sa;
        return (a.DataRegisto || '').localeCompare(b.DataRegisto || '');
      });
      setLeads(arr);
      setEvents(ev.data?.events || []);
      setResults(re.data?.results || []);
      setIdx(0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Erro a carregar');
    } finally { setLoading(false); }
  }, [tipoLead, scope]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/crm/botoes?contexto=lead_evolve')
      .then(r => setChipsCatalog((r.data?.botoes || []).filter(b => b.Activo)))
      .catch(() => {});
  }, []);

  // Batch attempts count para a lead actual + próximas 5
  useEffect(() => {
    const window = leads.slice(Math.max(0, idx), idx + 5).map(l => l.ID_Negocio);
    if (window.length === 0) return;
    api.post('/leads/attempts/counts', { ids: window })
      .then(r => setAttemptCounts(c => ({ ...c, ...(r.data?.counts || {}) })))
      .catch(() => {});
  }, [leads, idx]);

  const current = leads[idx];

  // Navegação por teclado
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowLeft' || e.key === 'n' || e.key === 'N') { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight' || e.key === 'm' || e.key === 'M') { e.preventDefault(); goNext(); }
      if (e.key === 'Escape') navigate('/leads/tratar');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, leads.length]);

  const goPrev = () => setIdx(i => Math.max(0, i - 1));
  const goNext = () => setIdx(i => Math.min(leads.length - 1, i + 1));

  const handleEvolve = async (payload) => {
    setSubmitting(true);
    try {
      await api.post(`/negocios/${current.ID_Negocio}/addState`, payload);
      // Remove a lead da lista e mantém idx (a próxima toma o lugar)
      setLeads(prev => prev.filter(l => l.ID_Negocio !== current.ID_Negocio));
      setIdx(i => Math.min(i, leads.length - 2));
      setToast({ type: 'success', msg: 'Lead evoluída — próxima carregada.' });
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast({ type: 'error', msg: e.response?.data?.detail || 'Erro a evoluir' });
      setTimeout(() => setToast(null), 4000);
    } finally { setSubmitting(false); }
  };

  const toggleChip = (k) => setChipsSel(prev => {
    const s = new Set(prev); if (s.has(k)) s.delete(k); else s.add(k); return s;
  });

  const quickNoAnswer = async () => {
    if (!current) return;
    setBusy('no_answer');
    try {
      const labels = chipsCatalog.filter(c => chipsSel.has(c.Codigo)).map(c => `[${c.Label}]`);
      const obs = labels.length > 0 ? `Não atendeu — ${labels.join(' ')}` : null;
      await api.post(`/negocios/${current.ID_Negocio}/attempt`,
        obs ? { tipo: 'call_no_answer', obs } : { tipo: 'call_no_answer' });
      setAttemptCounts(prev => ({
        ...prev,
        [current.ID_Negocio]: { count: (prev[current.ID_Negocio]?.count || 0) + 1, last_ts: new Date().toISOString() },
      }));
      setChipsSel(new Set());
      setToast({ type: 'success', msg: 'Não atendeu — registado. A avançar…' });
      setTimeout(() => setToast(null), 2000);
      goNext();
    } catch (e) { setToast({ type: 'error', msg: 'Falha' }); setTimeout(() => setToast(null), 3000); }
    finally { setBusy(null); }
  };

  const attemptsCount = useMemo(() => attemptCounts[current?.ID_Negocio]?.count || 0, [attemptCounts, current]);

  if (loading) return <div className="p-8 text-center text-[#6B7280]"><SpinnerGap size={24} className="animate-spin inline" /> A carregar…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F2] via-white to-[#F5F0E5]">
      <header className="bg-white border-b border-[#E5E7EB] px-6 py-3 flex items-center gap-4 sticky top-0 z-20">
        <button onClick={() => navigate('/leads/tratar')} data-testid="foco-back-kanban"
          className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829]">
          <ArrowLeft size={14} /> Kanban
        </button>
        <div className="h-4 w-px bg-[#E5E7EB]" />
        <h1 className="text-sm font-semibold text-[#0F1829]">Modo Foco</h1>
        <div className="flex items-center gap-1">
          {['CS', 'BC', 'BB'].map(t => (
            <button key={t} onClick={() => setTipoLead(t)} data-testid={`foco-tipo-${t}`}
              className={`text-xs px-2.5 py-1 rounded ${tipoLead === t ? 'bg-[#0F1829] text-white' : 'text-[#6B7280] hover:bg-[#F5F2EB]'}`}>
              {TIPO_LABELS[t]}
            </button>
          ))}
        </div>
        <ScopeToggle scope={scope} onChange={setScope} user={user} />
        <div className="ml-auto flex items-center gap-2 text-xs text-[#6B7280]">
          {leads.length > 0 && (
            <span data-testid="foco-progress" className="font-mono">
              {idx + 1} <span className="opacity-50">/</span> {leads.length}
            </span>
          )}
          <span className="text-[10px] text-[#9CA3AF] hidden md:inline">
            ← anterior · → seguinte · ESC sai
          </span>
        </div>
      </header>

      {error && <div className="max-w-3xl mx-auto mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {leads.length === 0 ? (
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <p className="text-lg font-semibold text-[#0F1829]">Sem leads pendentes</p>
          <p className="text-sm text-[#6B7280] mt-1">Não tens leads deste tipo/scope para tratar. Muda o filtro em cima ou vai fazer uma pausa merecida.</p>
          <button onClick={load} data-testid="foco-reload"
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#A8843C] text-white text-sm rounded">
            <ArrowsClockwise size={13} /> Recarregar
          </button>
        </div>
      ) : current ? (
        <main className="max-w-4xl mx-auto p-4 sm:p-8">
          <article
            data-testid={`foco-lead-${current.ID_Negocio}`}
            className="bg-white border-2 border-[#0F1829]/10 rounded-2xl shadow-lg overflow-hidden">
            <div className="p-5 sm:p-8 border-b border-[#F0EBDF]">
              <div className="flex items-start gap-3 mb-3">
                <a href={`/negocios/${current.ID_Negocio}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/negocios/${current.ID_Negocio}`); }}
                  className="text-xs font-mono font-medium text-[#A8843C] hover:underline mt-1">
                  #{current.ID_Negocio}
                </a>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] leading-tight flex-1">
                  {current.NomeContacto || current.Descricao || '—'}
                </h2>
                {attemptsCount > 0 && (
                  <span data-testid="foco-attempts-badge"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                    <Clock size={11} weight="bold" /> {attemptsCount} tentativa{attemptsCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {current.ClienteNome && (
                <p className="text-sm text-[#6B7280]" data-testid="foco-cliente-perfil">
                  Cliente: <PersonLink id={current.ID_Cliente} name={current.ClienteNome} className="font-bold" />
                </p>
              )}
              {current.CampanhaDesc && (
                <p className="text-xs text-[#0F1829] mt-1">
                  <span className="bg-[#0F1829]/8 px-1.5 py-0.5 rounded">{current.CampanhaDesc}</span>
                </p>
              )}
              <div className="flex flex-wrap items-center gap-4 mt-4">
                {current.TelemovelContacto && (
                  <a href={`tel:${current.TelemovelContacto}`}
                    onClick={() => logAttempt(current.ID_Negocio, 'call')}
                    data-testid="foco-phone"
                    className="inline-flex items-center gap-2 text-sm font-mono text-[#0F1829] hover:underline">
                    <Phone size={16} weight="bold" className="text-[#00C853]" /> {current.TelemovelContacto}
                  </a>
                )}
                {current.TelemovelContacto && (
                  <WhatsAppLink phone={current.TelemovelContacto} idNegocio={current.ID_Negocio}
                    firstName={(current.NomeContacto || '').split(' ')[0] || ''} size={14} className="w-8 h-8"
                    onOpen={() => logAttempt(current.ID_Negocio, 'whatsapp')} />
                )}
                {current.EmailContacto && (
                  <a href={`mailto:${current.EmailContacto}`}
                    onClick={() => logAttempt(current.ID_Negocio, 'email')}
                    className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#0F1829]">
                    <Envelope size={14} /> {current.EmailContacto}
                  </a>
                )}
              </div>
            </div>

            {/* Não atendeu com chips inline */}
            <div className="p-5 sm:p-8 border-b border-[#F0EBDF] bg-[#FAF8F2]">
              <p className="text-xs uppercase tracking-widest font-bold text-[#7A5B14] mb-2">Não atendeu / Tentativa</p>
              {chipsCatalog.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="foco-chips">
                  {chipsCatalog.map(c => {
                    const on = chipsSel.has(c.Codigo);
                    return (
                      <button key={c.Codigo} type="button" onClick={() => toggleChip(c.Codigo)}
                        data-testid={`foco-chip-${c.Codigo}`}
                        className={`text-xs px-2.5 py-1 rounded-full border ${on ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0F1829]/40'}`}>
                        {on ? '✓ ' : '+ '}{c.Label}
                      </button>
                    );
                  })}
                </div>
              )}
              <button type="button" onClick={quickNoAnswer} disabled={!!busy}
                data-testid="foco-noanswer-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-40">
                {busy === 'no_answer' ? <SpinnerGap size={14} className="animate-spin" /> : <PhoneSlash size={14} />}
                Registar &quot;Não atendeu&quot; e avançar
              </button>
            </div>

            {/* Evoluir Lead inline */}
            <div className="p-2 sm:p-4">
              <EvolvePanel
                lead={current}
                events={events}
                results={results}
                onClose={goNext}
                onSubmit={handleEvolve}
                submitting={submitting}
                preselectedState=""
                preselectedResult="1"
                errorMsg={null}
              />
            </div>
          </article>

          <div className="mt-4 flex items-center justify-between gap-2">
            <button onClick={goPrev} disabled={idx === 0} data-testid="foco-prev"
              className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm text-[#6B7280] hover:border-[#0F1829]/40 disabled:opacity-30">
              <CaretLeft size={14} /> Anterior
            </button>
            <button onClick={goNext} disabled={idx >= leads.length - 1} data-testid="foco-next"
              className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm text-[#6B7280] hover:border-[#0F1829]/40 disabled:opacity-30">
              Seguinte <CaretRight size={14} />
            </button>
          </div>
        </main>
      ) : null}

      {toast && (
        <div data-testid="foco-toast"
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded shadow-lg text-sm ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-[#0F1829] text-white'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
